import React, { useContext, useEffect, useRef, useState } from "react";
import axiosInstance from "../../../utils/Requests/axios";
import UserContext from "../../../utils/Contexts/UserContext";
import axios from "axios";
import toast from "react-hot-toast";
import LanguageContext from "../../../utils/Contexts/LanguageContext";
import { useCart } from "../../../utils/Contexts/CartContext";
import ExpressCheckout from "./Checkout";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useNavigate } from "react-router-dom";
import Constant from "../../Constants";
import {
  FUNDING,
  PayPalButtons,
  PayPalScriptProvider,
} from "@paypal/react-paypal-js";
import CheckIcon from "../../svg/check";

function CartBill({
  setActiveStep,
  activeStep,
  cartData,
  selectedMethod,
  setExpressMethod,
  expressMethod,
  guestTotal,
  getCartData,
  formValues,
  validateForm,
  linkTotal,
  nicheTotal,
  linkInserts,
  nicheEdits,
  guestPosts,
  setEmptyContentError,
  setAvailablePaymentMethods,
  stripeConfig,
  paypalConfig,
}) {
  const navigate = useNavigate();
  const [stripePromise, setStripePromise] = useState(null);
  const [isCouponExpanded, setIsCouponExpanded] = useState(false);
  const [isCreditExpanded, setIsCreditExpanded] = useState(false);
  const [promoCode, setPromoCode] = useState(null);
  const [showCouponError, setShowCouponError] = useState(false);
  const [showCreditError, setShowCreditError] = useState(false);
  const [balance, setBalance] = useState(null);
  const [manualBalance, setManualBalance] = useState(null);
  const { userData } = useContext(UserContext);
  const { t, language } = useContext(LanguageContext);
  const [isLoading, setIsLoading] = useState(false);
  const [showExpressPlaceholder, setShowExpressPlaceholder] = useState(true);
  const { paymentMethodTypes } = Constant();
  const { setCartCount } = useCart();

  const handleBalanceChange = (e) => {
    const value = parseFloat(e.target.value) || null;
    setManualBalance(value);
    setShowCreditError(false);
  };

  const creditsFetched = useRef(false);
  const getAvailableBalance = () => {
    if (creditsFetched.current) return;
    creditsFetched.current = true;
    const url = `/users/${userData?.uuid}/wallet/balance/available`;
    axiosInstance
      .get(url)
      .then((response) => {
        setBalance(response?.data?.amount);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    getAvailableBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (expressMethod && expressMethod !== "check") {
      setShowExpressPlaceholder(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expressMethod]);

  useEffect(() => {
    if (!stripeConfig?.key) {
      setAvailablePaymentMethods && setAvailablePaymentMethods([]);
      setShowExpressPlaceholder(false);
    } else {
      setAvailablePaymentMethods && setAvailablePaymentMethods(null);
      setShowExpressPlaceholder(true);
      if (!stripePromise) {
        setStripePromise(loadStripe(stripeConfig?.key));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeConfig]);

  const addCoupon = () => {
    axiosInstance
      .put("/cart/coupons", { code: promoCode })
      .then(() => {
        getCartData();
        setPromoCode("");
        setShowCouponError(false);
        setIsCouponExpanded(false);
      })
      .catch((error) => {
        console.log(error);
        setShowCouponError(true);
        toast.error(t("An error occurred while applying the coupon"));
      });
  };

  const handleRemoveCoupon = (itemUuid) => {
    const url = `/cart/coupons/${itemUuid}`;
    axiosInstance
      .delete(url)
      .then(() => {
        getCartData();
      })
      .catch((error) => {
        console.log(error);
        toast.error(t("An error occurred while removing the coupon"));
      });
  };

  const applyCredit = () => {
    if (manualBalance <= balance) {
      axiosInstance
        .put("/cart/credits", { value: manualBalance })
        .then(() => {
          getCartData();
          setManualBalance(null);
          setIsCreditExpanded(false);
        })
        .catch((error) => {
          console.log(error);
          setShowCreditError(true);
        });
    } else {
      setShowCreditError(true);
    }
  };

  const handleRemoveCredit = (itemUuid) => {
    const url = `/cart/credits/${itemUuid}`;
    axiosInstance
      .delete(url)
      .then(() => {
        getCartData();
      })
      .catch((error) => {
        console.log(error);
        toast.error(t("An error occurred while removing the credit"));
      });
  };

  const handleAddedCardPayment = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const isFormValid = validateForm(formValues);
    if (!isFormValid) {
      toast.error(t("Please correct the errors before submitting."));
      setIsLoading(false);
      return;
    }

    const [expMonth, expYear] = formValues?.expirationDate.split("/");
    try {
      const response = await axios.post(
        "https://api.stripe.com/v1/tokens",
        new URLSearchParams({
          "card[name]": formValues.nameOnCard,
          "card[number]": formValues.cardNumber,
          "card[exp_month]": expMonth,
          "card[exp_year]": `20${expYear}`,
          "card[cvc]": formValues.securityCode,
          "card[address_line1]": formValues.address,
          "card[address_line2]": formValues.optionalAddress,
          "card[address_country]": formValues.country,
          "card[address_state]": formValues.state,
          "card[address_city]": formValues.city,
          "card[address_zip]": formValues.zip,
        }),
        {
          headers: {
            Authorization: `Bearer ${stripeConfig?.key}`,
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      const token = response?.data?.id;
      const client_ip = response?.data?.client_ip;
      handleCartPayment({
        type: paymentMethodTypes.addCard,
        card: {
          details: {
            token,
            country: formValues.country,
            client_ip,
          },
          primary: formValues.isPrimary,
          save_card: formValues.saveCard,
        },
      });
    } catch (error) {
      console.error(
        "Error:",
        error.response ? error.response.data : error.message
      );
      toast.error(t("Payment process failed."));
      setIsLoading(false);
    }
  };

  const validateCartItems = () => {
    setIsLoading(true);
    if (activeStep === 1 && guestPosts.some((post) => !post?.post?.article)) {
      setEmptyContentError(true);
      setIsLoading(false);
      return;
    }
    if (activeStep === 1) {
      setIsLoading(false);
      setActiveStep(2);
    }
  };

  const handleCartPayment = (payload, redirect = true) => {
    setIsLoading(true);
    const url = `/cart/pay`;
    axiosInstance
      .post(url, { payment_method: payload })
      .then((res) => {
        const orderId = res?.data?.order_uuid;
        setCartCount(0);
        if (redirect) {
          navigate(`/cart/confirmation?orderId=${orderId}`);
        }
      })
      .catch((error) => {
        if (selectedMethod === "balance" && error?.status) {
          toast.error(
            t(
              "Your account does not have enough funds to cover Cart's Total. Please add funds or use a different payment method."
            )
          );
        } else if (
          selectedMethod === "balance" &&
          error?.code === "cart_item_and_offer_is_not_in_sync"
        ) {
          toast.error(t("Cart Item and Offer is not in sync anymore"));
        } else {
          toast.error(t("Failed to process payment"));
        }
        setIsLoading(false);
      });
  };

  const languageToLocaleMapping = {
    en: "en",
    gb: "en-GB",
    de: "de",
    dk: "da",
    it: "it",
    cn: "zh",
  };

  const options = {
    mode: "payment",
    amount: cartData?.total <= 0 ? 100 : cartData?.total * 100,
    currency: "usd",
    locale: languageToLocaleMapping[language],
    // Customizable with appearance API.
    appearance: {
      theme: "flat",
      variables: {
        fontFamily: "Segoe UI, system-ui, sans-serif",
        borderRadius: "10px",
      },
      disableAnimations: true,
    },
  };

  const createPayPalOrder = async () => {
    try {
      const response = await axiosInstance.post("/cart/intent", {
        type: paymentMethodTypes.paypal,
      });
      return response.data;
    } catch (error) {
      console.error(error);
      toast.error("Failed to create paypal order!");
      return null;
    }
  };

  return (
    <div className="col-12 confirm-cart-section px-xl-0">
      <div className="card text-white p-4 w-100">
        <h4 className="card-title">{t("Your Cart")}</h4>
        <div
          className="mb-3 mt-2 border-bottom"
          style={{ margin: "0px -25px" }}
        ></div>
        <div className="d-sm-flex flex-wrap justify-content-between d-xl-block border-bottom">
          <div className="w-100 w-sm-50 w-xl-100">
            {!!guestPosts?.length && (
              <div className="d-flex justify-content-between mb-3">
                <span
                  className="text-dark responsive-text-affiliation"
                  style={{ opacity: 0.8 }}
                >
                  {guestPosts?.length}x {t("Guest Post(s)")}:
                </span>
                <span className="text-dark fw-bold responsive-text-affiliation">
                  ${guestTotal}
                </span>
              </div>
            )}
            {!!linkInserts?.length && (
              <div className="d-flex justify-content-between mb-3">
                <span
                  className="text-dark responsive-text-affiliation"
                  style={{ opacity: 0.8 }}
                >
                  {linkInserts?.length}x {t("Link Insert(s)")}:
                </span>
                <span className="text-dark fw-bold responsive-text-affiliation">
                  ${linkTotal}
                </span>
              </div>
            )}
            {!!nicheEdits?.length && (
              <div className="d-flex justify-content-between mb-3">
                <span
                  className="text-dark responsive-text-affiliation"
                  style={{ opacity: 0.8 }}
                >
                  {nicheEdits?.length}x {t("Niche Edit(s)")}:
                </span>
                <span className="text-dark fw-bold responsive-text-affiliation">
                  ${nicheTotal}
                </span>
              </div>
            )}
            {!!cartData?.fees?.length &&
              cartData?.fees?.map((fees, i) => (
                <div key={i}>
                  <div className="d-flex justify-content-between mb-1">
                    <span
                      className="text-dark responsive-text-affiliation"
                      style={{ opacity: 0.8 }}
                    >
                      {t("Processing Fee")}:
                    </span>

                    <span className="text-dark fw-bold responsive-text-affiliation">
                      ${fees?.value}
                    </span>
                  </div>
                  <div style={{ width: "75%" }}>
                    <small
                      className="text-secondary border-lg-bottom pb-3"
                      style={{ fontSize: "14px" }}
                    >
                      {t("For credit card payments under $50, a")}{" "}
                      <span className="text-decoration-underline">
                        {t("processing fee")}
                      </span>{" "}
                      {t("will apply")}
                    </small>
                  </div>
                </div>
              ))}
          </div>
          <div>
            {cartData?.deductions?.coupons.length > 0 ? (
              cartData?.deductions?.coupons.map((coupon, index) => (
                <div
                  className="d-flex justify-content-between gap-8 gap-xl-0 mt-0 mt-sm-0 mt-md-0 mt-xl-4"
                  key={index}
                >
                  <div className="d-flex gap-2 align-items-center">
                    <CheckIcon />
                    <div className="d-flex flex-column gap-1">
                      <span className="text-dark responsive-text-coupon">
                        {coupon?.code}{" "}
                        <span className="text-secondary ms-1 responsive-text-applied">
                          {t("applied")}
                        </span>
                      </span>
                      <small className="text-secondary small-txt">
                        -${coupon?.deduction} (
                        {`${coupon?.type === 1 ? "$" : ""}${coupon?.value}${
                          coupon?.type === 1 ? "" : "%"
                        } ${t("off")}`}
                        )
                      </small>
                    </div>
                  </div>
                  <button
                    className="btn btn-danger d-flex align-items-center justify-content-center"
                    style={{
                      whiteSpace: "nowrap",
                      height: "35px",
                      width: "71px",
                    }}
                    disabled={isLoading && activeStep === 2}
                    onClick={() => handleRemoveCoupon(coupon?.uuid)}
                  >
                    {t("Remove")}
                  </button>
                </div>
              ))
            ) : (
              <div className="mt-4 mt-sm-4 mt-md-0 mt-xl-4">
                <div
                  className="d-flex align-items-center justify-content-between gap-8 gap-xl-0"
                  onClick={() => setIsCouponExpanded(!isCouponExpanded)}
                >
                  <label
                    htmlFor="promoCode"
                    className="form-label text-dark mb-0 responsive-text-affiliation cursor-pointer"
                    style={{ opacity: 0.8 }}
                  >
                    {t("Add a gift card / promotion code")}
                  </label>
                  <button
                    className="btn btn-link p-0 text-dark"
                    style={{ textDecoration: "none" }}
                  >
                    <i
                      style={{ opacity: 0.8 }}
                      className={`bi ${
                        isCouponExpanded ? "bi-chevron-up" : "bi-chevron-down"
                      }`}
                    ></i>
                  </button>
                </div>
                {isCouponExpanded && (
                  <div className="mt-4 mt-sm-4 mt-md-0 mt-xl-4">
                    <div className="d-flex gap-3">
                      <input
                        type="text"
                        className={`form-control input-credit ${
                          showCouponError ? "border-danger" : ""
                        }`}
                        id="promoCode"
                        placeholder={t("Enter code")}
                        value={promoCode}
                        onChange={(e) => {
                          setPromoCode(e.target.value);
                          setShowCouponError(false);
                        }}
                      />
                      <button
                        className="btn btn-outline-primary responsive-text-affiliation"
                        style={{
                          whiteSpace: "nowrap",
                          height: "47px",
                          width: "103px",
                        }}
                        onClick={() => {
                          if (
                            promoCode &&
                            !showCouponError &&
                            !(isLoading && activeStep === 2)
                          ) {
                            addCoupon();
                          }
                        }}
                      >
                        {t("Apply")}
                      </button>
                    </div>
                    {showCouponError && (
                      <small className="text-danger mt-1">
                        <i className="bi bi-exclamation-triangle-fill"></i>{" "}
                        {t("This code is invalid")}
                      </small>
                    )}
                  </div>
                )}
              </div>
            )}
            {cartData?.deductions?.credits?.length > 0 ? (
              cartData?.deductions?.credits?.map((credit, index) => (
                <div
                  className="d-flex justify-content-between mb-1 mt-4"
                  key={index}
                >
                  <div className="d-flex gap-1 align-items-center">
                    <CheckIcon />
                    <div className="d-flex flex-column gap-1">
                      <span className="text-dark responsive-text-coupon">
                        {t("Credits")}{" "}
                        <small className="text-secondary responsive-text-applied">
                          {t("applied")}
                        </small>
                      </span>
                      <small className="text-secondary mb-1 small-txt">
                        -${credit?.deduction}
                      </small>
                    </div>
                  </div>
                  <button
                    className="btn btn-danger d-flex align-items-center justify-content-center "
                    style={{
                      whiteSpace: "nowrap",
                      height: "35px",
                      width: "71px",
                    }}
                    disabled={isLoading && activeStep === 2}
                    onClick={() => handleRemoveCredit(credit?.uuid)}
                  >
                    {t("Remove")}
                  </button>
                </div>
              ))
            ) : (
              <div className="mt-4 border-lg-bottom pb-4">
                <div
                  className="d-flex align-items-center justify-content-between"
                  onClick={() => setIsCreditExpanded(!isCreditExpanded)}
                >
                  <label
                    htmlFor="credits"
                    className="form-label text-dark mb-0 responsive-text-affiliation cursor-pointer"
                    style={{ opacity: 0.8 }}
                  >
                    {t("Use Credits to Pay")}
                  </label>
                  <button
                    className="btn btn-link p-0 text-dark"
                    style={{ textDecoration: "none" }}
                  >
                    <i
                      style={{ opacity: 0.8 }}
                      className={`bi ${
                        isCreditExpanded ? "bi-chevron-up" : "bi-chevron-down"
                      }`}
                    ></i>
                  </button>
                </div>
                {isCreditExpanded && (
                  <div className="mt-2">
                    <div className="d-flex">
                      <small className="text-secondary credits-available">
                        {balance.toFixed(2)} {t("credits available")}
                      </small>
                    </div>
                    <div className="d-flex gap-3 mt-2">
                      <input
                        style={{
                          width: "203px",
                          height: "47px",
                        }}
                        type="number"
                        className={`form-control input-credit ${
                          showCreditError ? "border-danger" : ""
                        }`}
                        id="credits"
                        placeholder="0.00"
                        value={manualBalance}
                        onChange={handleBalanceChange}
                      />
                      <button
                        className="btn btn-outline-primary responsive-text-affiliation"
                        style={{
                          whiteSpace: "nowrap",
                          height: "47px",
                          width: "103px",
                        }}
                        onClick={() => {
                          if (
                            manualBalance &&
                            !manualBalance <= 0 &&
                            !(isLoading && activeStep === 2)
                          ) {
                            applyCredit();
                          }
                        }}
                      >
                        {t("Apply")}
                      </button>
                    </div>
                    {showCreditError && (
                      <small className="text-danger mt-1">
                        <i className="bi bi-exclamation-triangle-fill"></i>{" "}
                        {t("Entered value exceeds available credits")}
                      </small>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="d-flex justify-content-between mt-4">
          <strong className="text-dark" style={{ fontSize: "18px" }}>
            {t("Total to Pay")}:
          </strong>
          <strong className="text-dark" style={{ fontSize: "18px" }}>
            ${cartData?.total}
          </strong>
        </div>
        {showExpressPlaceholder ? (
          <div className="placeholder-glow w-100 w-sm-50 w-md-50 w-xl-100 mt-4 align-self-center">
            <div
              className="placeholder col-12 rounded"
              style={{ height: "3em", backgroundColor: "#677788" }}
            ></div>
          </div>
        ) : (
          <>
            {(!expressMethod || !stripePromise) &&
              selectedMethod !== "paypal" && (
                <button
                  className="btn btn-primary mt-4 rounded align-self-center cart-pay-button"
                  onClick={(e) => {
                    if (activeStep === 1) {
                      validateCartItems();
                    } else {
                      if (selectedMethod === "addCard") {
                        handleAddedCardPayment(e);
                      } else if (selectedMethod.includes("card_")) {
                        handleCartPayment({
                          type: paymentMethodTypes.card,
                          card: { uuid: selectedMethod.split("_")[1] },
                        });
                      } else if (selectedMethod === "balance") {
                        handleCartPayment({ type: paymentMethodTypes.balance });
                      } else {
                        toast.error(t("No payment method selected."));
                      }
                    }
                  }}
                  disabled={isLoading || (activeStep === 2 && !selectedMethod)}
                >
                  {isLoading ? (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  ) : activeStep === 1 ? (
                    t("Continue")
                  ) : (
                    t("Confirm & Pay")
                  )}
                </button>
              )}
          </>
        )}
        {expressMethod && stripePromise && (
          <div
            className={`${
              showExpressPlaceholder
                ? ""
                : "w-100 w-sm-50 w-md-50 w-xl-100 mt-4 align-self-center"
            }`}
          >
            <Elements stripe={stripePromise} options={options}>
              <ExpressCheckout
                expressMethod={expressMethod}
                setExpressMethod={setExpressMethod}
                handleCartPayment={handleCartPayment}
                setShowExpressPlaceholder={setShowExpressPlaceholder}
                setAvailablePaymentMethods={setAvailablePaymentMethods}
              />
            </Elements>
          </div>
        )}

        {selectedMethod === "paypal" && (
          <div className="w-100 w-sm-50 w-md-50 w-xl-100 mt-4 align-self-center">
            <PayPalScriptProvider
              style={{ width: "100%" }}
              options={{ clientId: paypalConfig?.key }}
            >
              <PayPalButtons
                style={{
                  layout: "vertical",
                  borderRadius: 8,
                  label: "pay",
                  height: 42,
                }}
                fundingSource={FUNDING.PAYPAL}
                createOrder={async () => {
                  setIsLoading(true);
                  const orderData = await createPayPalOrder();
                  setIsLoading(false);
                  return orderData?.id;
                }}
                onApprove={async (data) => {
                  try {
                    setIsLoading(true);
                    handleCartPayment({
                      type: paymentMethodTypes.paypal,
                      secret: data.orderID,
                    });
                  } catch (error) {
                    console.error(error);
                    toast.error("Failed to capture paypal payment!");
                  } finally {
                    setIsLoading(false);
                  }
                }}
                onCancel={() => {
                  setIsLoading(false);
                }}
                onError={() => {
                  toast.error("Error occured during paypal payment!");
                  setIsLoading(false);
                }}
                disabled={isLoading}
              />
            </PayPalScriptProvider>
          </div>
        )}
      </div>
      <div
        className="text-center text-secondary py-2"
        style={{ fontWeight: "400", fontSize: "14px" }}
      >
        <i className="bi bi-headset"></i> {t("Need Help")}?{" "}
        <span className="text-primary text-decoration-underline">
          {t("Contact Us")}
        </span>
      </div>
    </div>
  );
}

export default CartBill;
